import React, { FC, useEffect, useState } from 'react';
import Button from '~ComponentsImproved/Button';
import Modal from '~ComponentsImproved/Modal';
import { Printer } from '~reducers';
import BluetoothTroubleShooting from './BluetoothTroubleShooting';
import USBTroubleShooting from './USBTroubleShooting';
import LANTroubleShooting from './LANTroubleShooting';
import { uniqueId } from 'lodash';
import {
  PrinterStatusResponse,
  useBridgeApi,
  useButton,
  useI18n,
  useLogger,
  usePrinterApi,
} from '~Hooks';
import styled from '@emotion/styled';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { CircleSpinner } from 'react-spinners-kit';
import { BlackButton, Box } from '~Styles';
import Icon from '~ComponentsImproved/Icon';
import CloseIcon from '~Assets/icons/close.svg';

const TroubleShootWrapper = styled.div`
  padding: 40px;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: calc(100% - 64px);
  max-width: 980px;
  height: calc(100% - 64px);
  overflow-y: auto;
  text-align: center;
  position: relative;
  box-sizing: border-box;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
`;

const CloseButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  height: 40px;
  width: 40px;
  background: var(--colorBlueGray100);
`;

export const TroubleShootButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  width: 100%;
  max-width: 600px;
  align-items: center;
  justify-content: center;

  button {
    max-width: 300px;
  }
`;

export const TroubleShootingTextWrapper = styled.div`
  max-width: 600px;
`;

interface Props {
  printer: Printer;
  testPrint: () => void;
  assignStation: () => void;
}

const PrinterTroubleshooting: FC<Props> = ({ printer, testPrint, assignStation }) => {
  const logger = useLogger('printer-troubleshooting');
  const [showGuide, setShowGuide] = useState(false);
  const isBlueTooth =
    printer.id.toLowerCase().includes('bluetooth') || printer.id.toLowerCase().includes('bt:');
  const isUsb = printer.id.toLowerCase().includes('usb');
  const { i18n } = useI18n();
  const printerApi = usePrinterApi();
  const bridgeApi = useBridgeApi();
  const sessionId = uniqueId(); // For datadog logging purposes
  const [printerStatus, setPrinterStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const getStatusesWithTimeout = async (
    printerId: string,
    timeout = 2000,
  ): Promise<PrinterStatusResponse> => {
    // Create a promise that rejects after the timeout
    const timeoutPromise: Promise<PrinterStatusResponse> = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), timeout),
    );

    return Promise.race([bridgeApi.getPrinterStatus(printerId), timeoutPromise]);
  };

  const setCorrectPrinterStatus = (statuses: PrinterStatusResponse) => {
    if (statuses.coverIsOpen) {
      setPrinterStatus('COVER_OPEN');
    } else if (statuses.paperEmpty) {
      setPrinterStatus('PAPER_EMPTY');
    } else {
      setPrinterStatus('ENABLE_TROUBLESHOOT');
    }
  };

  const checkPrinterStatus = async () => {
    setLoading(true);
    setPrinterStatus('');
    const printerData = {
      printerId: printer.id,
      printerName: printerApi.getNameFromId(printer.id),
      model: printer.model,
      createdAt: printer.createdAt,
      lastPrinted: printer.lastPrinted,
    };

    try {
      const statuses: PrinterStatusResponse = await getStatusesWithTimeout(printer.id);
      logger.info('Test printer status ' + printer.id, {
        troubleShooting: {
          statuses,
          printerData,
          troubleShootingSessionId: sessionId,
        },
      });

      setLoading(false);
      setCorrectPrinterStatus(statuses);
    } catch (error) {
      // If it goes into this error block, it means that the printer is not reachable

      if (error instanceof Error && error.message === 'Timeout') {
        logger.warn('Printer status request timed out', {
          troubleShooting: {
            printerData,
            troubleShootingSessionId: sessionId,
          },
        });

        setPrinterStatus('ENABLE_TROUBLESHOOT');
        setLoading(false);
      } else {
        logger.warn('Error fetching printer status', {
          troubleShooting: {
            errorMessage: error ?? 'Unknown error',
            printerData,
            troubleShootingSessionId: sessionId,
          },
        });
        setPrinterStatus('ENABLE_TROUBLESHOOT');
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!showGuide) {
      return;
    }

    checkPrinterStatus();
  }, [showGuide]);

  const closeGuide = () => {
    setShowGuide(false);
    setPrinterStatus('');
  };

  const StatusTitle = {
    COVER_OPEN: i18n('Printer.Troubleshooting.CoverOpen'),
    PAPER_EMPTY: i18n('Printer.Troubleshooting.OutOfPaper'),
  };

  const StatusText = {
    COVER_OPEN: i18n('Printer.Troubleshooting.CoverOpen.Desc'),
    PAPER_EMPTY: i18n('Printer.Troubleshooting.OutOfPaper.Desc'),
  };

  const troubleShootingButton = useButton({
    onClick: () => {
      logger.info('Troubleshoot button pressed', {
        troubleShooting: {
          printer,
          troubleShootingSessionId: sessionId,
        },
      });
      setShowGuide(true);
    },
  });

  return (
    <>
      <BlackButton {...troubleShootingButton} size="small">
        {i18n('Printer.Troubleshooting')}
      </BlackButton>
      <Modal isShowing={showGuide} backdropClose hide={() => setShowGuide(false)} noPadding>
        <TroubleShootWrapper>
          <CloseButton onTouchStart={() => setShowGuide(false)}>
            <Icon src={CloseIcon} color="#000" />
          </CloseButton>
          <InnerWrapper>
            {loading ? (
              <Box display="flex" flexDirection="column" alignItems="center">
                <Heading level={2} color="#fff" bottom={32}>
                  {i18n('Printer.Troubleshooting.CheckingStatus')}
                </Heading>
                <CircleSpinner />
              </Box>
            ) : printerStatus === 'ENABLE_TROUBLESHOOT' ? (
              <>
                {isBlueTooth ? (
                  <BluetoothTroubleShooting
                    printer={printer}
                    closeGuide={closeGuide}
                    testPrint={testPrint}
                    assignStation={assignStation}
                    sessionId={sessionId}
                  />
                ) : isUsb ? (
                  <USBTroubleShooting
                    printer={printer}
                    closeGuide={closeGuide}
                    testPrint={testPrint}
                    assignStation={assignStation}
                    sessionId={sessionId}
                  />
                ) : (
                  <LANTroubleShooting
                    printer={printer}
                    closeGuide={closeGuide}
                    testPrint={testPrint}
                    assignStation={assignStation}
                    sessionId={sessionId}
                  />
                )}
              </>
            ) : printerStatus === 'COVER_OPEN' || printerStatus === 'PAPER_EMPTY' ? (
              <>
                <TroubleShootingTextWrapper>
                  <Heading level={1} color="#fff" bottom={16}>
                    {StatusTitle[printerStatus]}
                  </Heading>
                  <Paragraph level={2} color="var(--colorBlueGray200)">
                    {StatusText[printerStatus]}
                  </Paragraph>
                </TroubleShootingTextWrapper>

                <TroubleShootButtons>
                  <Button
                    text={i18n('Printer.Troubleshooting.OK')}
                    styleType="OK"
                    onTouchStart={() => {
                      setShowGuide(false);
                    }}
                  />
                </TroubleShootButtons>
              </>
            ) : null}
          </InnerWrapper>
        </TroubleShootWrapper>
      </Modal>
    </>
  );
};

export default PrinterTroubleshooting;
